import dayjs from 'dayjs';
import {
    getSmartScheduleDateKey,
    getSmartScheduleMinutesOfDay,
    SmartScheduleDateInput,
    toSmartScheduleKst,
} from './date';
import {
    SmartScheduleEvent,
    SmartScheduleKind,
    SmartScheduleOperatingHours,
    SmartScheduleStatus,
} from './types';

export const normalizeSmartScheduleSearchText = (value: string): string =>
    value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ko-KR');

export const matchesSmartScheduleSearch = (event: SmartScheduleEvent, query: string): boolean => {
    const tokens = normalizeSmartScheduleSearchText(query).split(' ').filter(Boolean);
    if (!tokens.length) return true;
    const haystack = event.searchText || normalizeSmartScheduleSearchText([
        event.title,
        event.subtitle,
        event.coachName,
        ...event.memberNames,
        event.membershipName,
    ].filter(Boolean).join(' '));
    return tokens.every((token) => haystack.includes(token));
};

export interface SmartScheduleEventFilter {
    coachIds?: string[];
    kinds?: SmartScheduleKind[];
    statuses?: SmartScheduleStatus[];
    searchText?: string;
}

export const filterSmartScheduleEvents = (
    events: SmartScheduleEvent[],
    { coachIds, kinds, statuses, searchText = '' }: SmartScheduleEventFilter
): SmartScheduleEvent[] =>
    events.filter(
        (event) =>
            (!coachIds?.length || coachIds.includes(event.coachId)) &&
            (!kinds?.length || kinds.includes(event.kind)) &&
            (!statuses?.length || statuses.includes(event.status)) &&
            matchesSmartScheduleSearch(event, searchText)
    );

export const getSmartScheduleEventsForDay = (
    events: SmartScheduleEvent[],
    day: SmartScheduleDateInput
): SmartScheduleEvent[] => {
    const dayKey = getSmartScheduleDateKey(day);
    return events.filter((event) => getSmartScheduleDateKey(event.startTime) === dayKey);
};

export const sortSmartScheduleEvents = (events: SmartScheduleEvent[]): SmartScheduleEvent[] =>
    [...events].sort(
        (left, right) =>
            dayjs(left.startTime).valueOf() - dayjs(right.startTime).valueOf() ||
            left.coachName.localeCompare(right.coachName, 'ko') ||
            left.id.localeCompare(right.id)
    );

export const countConfirmedSessions = (events: SmartScheduleEvent[], coachId?: string): number =>
    events.filter(
        (event) =>
            event.isConfirmed &&
            (event.kind === 'private' || event.kind === 'group') &&
            (!coachId || event.coachId === coachId)
    ).length;

export const countConfirmedSessionsByCoach = (events: SmartScheduleEvent[]): Record<string, number> =>
    events.reduce<Record<string, number>>((counts, event) => {
        if (!event.coachId || !event.isConfirmed || (event.kind !== 'private' && event.kind !== 'group')) return counts;
        counts[event.coachId] = (counts[event.coachId] || 0) + 1;
        return counts;
    }, {});

export type VisibleSmartScheduleKind = Exclude<SmartScheduleKind, 'off'>;
export type SmartScheduleCountBreakdown = Record<VisibleSmartScheduleKind, number>;

export const getVisibleSmartScheduleCountBreakdown = (
    events: SmartScheduleEvent[]
): SmartScheduleCountBreakdown =>
    events.reduce<SmartScheduleCountBreakdown>(
        (counts, event) => {
            if (event.kind !== 'off') counts[event.kind] += 1;
            return counts;
        },
        { private: 0, group: 0, request: 0, general: 0, opportunity: 0 }
    );

/** Counts every schedule item shown in the default smart calendar, excluding OFF blocks. */
export const countVisibleSmartScheduleEvents = (events: SmartScheduleEvent[]): number =>
    Object.values(getVisibleSmartScheduleCountBreakdown(events)).reduce((total, count) => total + count, 0);

/** Counts visible schedule items per coach using the same OFF-excluding rule as the total count. */
export const countVisibleSmartScheduleEventsByCoach = (
    events: SmartScheduleEvent[]
): Record<string, number> =>
    events.reduce<Record<string, number>>((counts, event) => {
        if (event.kind === 'off' || !event.coachId) return counts;
        const coachId = String(event.coachId);
        counts[coachId] = (counts[coachId] || 0) + 1;
        return counts;
    }, {});

/** Resolves only real group-session records, not legacy 1:2/1:3 rows inferred from PT schedules. */
export const resolveSmartScheduleGroupSessionId = (event: SmartScheduleEvent): string => {
    if (event.kind !== 'group') return '';
    if (event.groupSessionId) return event.groupSessionId;

    // Backward compatibility for already-normalized/cached events created before
    // groupSessionId became part of the common model.
    const groupSessionItem = event.rawItems.find(
        (rawItem: any) =>
            rawItem?.groupSessionId ||
            rawItem?.templateId ||
            Array.isArray(rawItem?.allowedTicketIds)
    );
    if (!groupSessionItem) return '';

    return String(
        groupSessionItem.groupSessionId ||
        groupSessionItem.sessionId ||
        groupSessionItem._id ||
        groupSessionItem.id ||
        ''
    );
};

/** Expands the default 06:00-24:00 board to 00:00-24:00 when needed. */
export const getSmartScheduleOperatingHours = (
    events: SmartScheduleEvent[],
    defaultStartHour = 6,
    defaultEndHour = 24
): SmartScheduleOperatingHours => {
    const hasOutsideHours = events.some((event) => {
        const startMinutes = getSmartScheduleMinutesOfDay(event.startTime);
        const end = toSmartScheduleKst(event.endTime);
        const endsOnLaterDay = getSmartScheduleDateKey(event.startTime) !== getSmartScheduleDateKey(event.endTime);
        const endMinutes = endsOnLaterDay ? 24 * 60 : getSmartScheduleMinutesOfDay(end);
        return startMinutes < defaultStartHour * 60 || endMinutes > defaultEndHour * 60;
    });

    return {
        startHour: hasOutsideHours ? 0 : defaultStartHour,
        endHour: defaultEndHour,
        isExpanded: hasOutsideHours,
    };
};

export const getSmartScheduleEventPosition = (
    event: Pick<SmartScheduleEvent, 'startTime' | 'endTime'>,
    startHour = 0
): { offsetMinutes: number; durationMinutes: number } => ({
    offsetMinutes: Math.max(0, getSmartScheduleMinutesOfDay(event.startTime) - startHour * 60),
    durationMinutes: Math.max(0, toSmartScheduleKst(event.endTime).diff(toSmartScheduleKst(event.startTime), 'minute', true)),
});
